<template>
  <div id="customer-return-create" class="group-setup">
    <v-progress-linear indeterminate color="blue" v-if="loading"></v-progress-linear>

    <v-layout row v-if="errors">
      <v-flex>
        <Alert @dismissed="onDismissed" :text="errors"></Alert>
      </v-flex>
    </v-layout>

    <v-form v-if="!loading">
      <v-row>
        <v-col cols="12" md="12" sm="12">
          <v-card class="pa-5" flat>
            <HeaderDividerBlock headerText="Create Purchase Return" />

            <v-row>
              <v-col cols="6" sm="6">
                <v-col class="pa-0 ma-0">
                  <p class="text-title">Customer</p>
                </v-col>
                <v-text-field outlined dense :value="customerName" disabled></v-text-field>
              </v-col>

              <v-col cols="6" sm="6">
                <v-col class="pa-0 ma-0">
                  <p class="text-title">Return Date</p>
                </v-col>
                <DatePicker v-model="form.return_date" placeholder="Return Date" :max="new Date().toISOString().substr(0, 10)"></DatePicker>
              </v-col>

              <v-col cols="6" sm="6">
                <v-col class="pa-0 ma-0">
                  <p class="text-title">Invoice</p>
                </v-col>
                <v-autocomplete outlined dense v-model="form.invoice_id" clearable placeholder="Select Invoice" :items="invoices" item-text="invoice_number" item-value="id" @change="onInvoiceSelect">
                  <template v-slot:item="{ item }">
                    <v-list-item-content>
                      <v-list-item-title>{{ item.invoice_number }}</v-list-item-title>
                      <v-list-item-subtitle>
                        {{ currencyCode }}{{ formatNumber(item.total_invoice) }}
                        <v-chip x-small :color="item.is_paid ? 'green' : 'orange'" class="ml-2" text-color="white">
                          {{ item.is_paid ? 'Paid' : 'Unpaid' }}
                        </v-chip>
                      </v-list-item-subtitle>
                    </v-list-item-content>
                  </template>
                </v-autocomplete>
              </v-col>

              <v-col cols="12" sm="12">
                <v-col class="pa-0 ma-0">
                  <p class="text-title">Reason for Return</p>
                </v-col>
                <v-text-field outlined dense v-model="form.reason_for_return" clearable placeholder="Reason for Return"></v-text-field>
              </v-col>
            </v-row>

            <v-col cols="12" md="12" sm="12">
              <CustomerReturnFormItems :invoice-items="invoiceItems" @items="handleItems" />
            </v-col>

            <v-col cols="12" md="6" sm="12">
              <v-col>
                <v-col class="ma-0 pa-0">
                  <p class="text-h5 font-weight-bold">Summary</p>
                </v-col>
                <v-col class="px-7 pa-10 total-section">
                  <v-row class="justify-space-between">
                    <p class="text-subtitle-1 font-weight-bold">Sub Total</p>
                    <p>{{ currencyCode }}{{ formatNumber(subTotal) }}</p>
                  </v-row>
                  <v-row class="justify-space-between">
                    <p class="text-subtitle-1 font-weight-bold">Grand Total</p>
                    <p>{{ currencyCode }}{{ formatNumber(subTotal) }}</p>
                  </v-row>
                </v-col>
              </v-col>
            </v-col>

            <v-card-actions class="mt-10">
              <v-row>
                <v-col cols="12" md="10" xs="6"></v-col>
                <v-col cols="12" md="2" xs="6">
                  <v-btn large color="#00B3FF" dark depressed block @click="submit">Create</v-btn>
                </v-col>
              </v-row>
            </v-card-actions>
          </v-card>
        </v-col>
      </v-row>
    </v-form>

    <Snackbars :text="successMsg" :snackbar="snackbar" color="success" @classSnackbar="closeSnackbar" />
  </div>
</template>

<script>
import DatePicker from "@/components/shared/DatePicker";
import Alert from "@/components/shared/Alert";
import Snackbars from "@/components/shared/Snackbars";
import HeaderDividerBlock from "@/components/shared/HeaderDividerBlock";
import CustomerReturnFormItems from "@/components/customers/CustomerReturnFormItems";
import { CustomerService } from "@/services/customer-service.js";
import { InvoiceService } from "@/services/invoice-service.js";
import { createNamespacedHelpers } from "vuex";
const { mapGetters: mapSettingsGetters } = createNamespacedHelpers("settings");
const { mapGetters: mapAuthGetters } = createNamespacedHelpers("auth");

export default {
  name: "CustomerCreateReturn",
  components: {
    Alert,
    Snackbars,
    DatePicker,
    HeaderDividerBlock,
    CustomerReturnFormItems,
  },
  data: () => ({
    loading: false,
    errors: null,
    snackbar: false,
    successMsg: "",
    customerName: "",
    customerId: null,
    invoices: [],
    invoiceItems: [],
    subTotal: 0,
    form: {
      return_date: new Date().toISOString().substr(0, 10),
      reason_for_return: "",
      customer_id: null,
      invoice_id: null,
      invoice_number: null,
      branch_id: null,
      store_id: null,
      items: [],
    },
  }),
  computed: {
    ...mapSettingsGetters(["currencyCode"]),
    ...mapAuthGetters({ profile: "profile" }),
  },
  created() {
    this.$store.dispatch("auth/getProfile").then(() => {
      this.loadCustomerData();
    }).catch(() => {
      this.loadCustomerData();
    });
    this.loadInvoices();
  },
  methods: {
    formatNumber(value) {
      return Number(value || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },
    onDismissed() {
      this.errors = null;
    },
    closeSnackbar() {
      this.snackbar = false;
    },
    async loadCustomerData() {
      try {
        const res = await CustomerService.getCustomerInfo(this.$route.params.uuid);
        const customer = res.data.data || res.data;
        this.customerName = customer.company_name || customer.first_name + " " + customer.last_name;
        this.customerId = customer.id;
        this.form.customer_id = customer.id;
      } catch (e) {
        console.error("Failed to load customer", e);
      }
    },
    async loadInvoices() {
      try {
        const res = await CustomerService.getInvoices(this.$route.params.uuid, 1, 1000);
        this.invoices = (res.invoices || []).filter((inv) => inv.is_post==1);
      } catch (e) {
        console.error("Failed to load invoices", e);
        this.invoices = [];
      }
    },
    async onInvoiceSelect(invoiceId) {
      if (invoiceId) {
        const invoice = this.invoices.find((inv) => inv.id === invoiceId);
        this.form.invoice_id = invoiceId;
        this.form.invoice_number = invoice ? invoice.invoice_number : null;
        this.form.branch_id = invoice ? invoice.branch_id : null;
        this.form.store_id = invoice ? invoice.store_id : null;
        try {
          const items = await InvoiceService.getItemsByInvoiceId(invoiceId);
          this.invoiceItems = (items || []).map((item) => ({
            ...item,
            quantity_returned: 0,
            condition: "Good",
            reason: "",
            cost: 0,
          }));
        } catch (e) {
          this.invoiceItems = [];
        }
      } else {
        this.form.invoice_id = null;
        this.form.invoice_number = null;
        this.form.branch_id = null;
        this.form.store_id = null;
        this.invoiceItems = [];
      }
    },
    handleItems(items) {
      this.form.items = items;
      this.subTotal = items.reduce((sum, item) => sum + (Number(item.quantity_returned) * Number(item.unit_price) || 0), 0);
    },
    async submit() {
      try {
        this.loading = true;
        this.errors = null;
        const payload = {
          return_date: this.form.return_date,
          reason_for_return: this.form.reason_for_return,
          customer_id: this.form.customer_id,
          invoice_id: this.form.invoice_id || undefined,
          invoice_number: this.form.invoice_number || undefined,
          branch_id: this.form.branch_id,
          store_id: this.form.store_id || undefined,
          items: this.form.items.map((item) => ({
            product_code: item.product_code,
            product_id: item.product_id,
            batch_id: item.batch_id,
            quantity: Number(item.quantity_returned),
            unit_price: Number(item.unit_price),
            cost: Number(item.cost || 0),
            tax_percent: Number(item.tax_percent || 0),
            discount_percent: Number(item.discount || 0),
            reason: item.reason || undefined,
            condition: item.condition || "Good",
          })),
        };
        const response = await CustomerService.createCustomerReturn(payload);
        this.successMsg = response.data.message || "Return created successfully";
        this.snackbar = true;
        this.$router.push({ name: "Customer", params: { uuid: this.$route.params.uuid } });
      } catch (error) {
        console.error(error);
        const msg = error.response?.data?.message || "Failed to create return";
        this.errors = Array.isArray(msg) ? msg : [msg];
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>
